/*
 * Copyright (c) 2020. American Express Company
 */

// external
// import $ from 'jquery';
import _ from 'lodash';
import React, { ReactComponentElement } from 'react';
import BasicDrawer from './common/BasicDrawer';

// models and services
import { AccountModel } from 'src/types/account.model';
import { InstitutionModel } from 'src/types/institution.model';
import AccountService from '../../services/account.service';
import FormatService from '../../services/format.service';
import InstitutionService from '../../services/institution.service';
import LiteralsService from '../../services/literals/LiteralsService';
import MixpanelService from '../../services/mixpanel.service';
import NavigationService from '../../services/navigation.service';
import AccountDrawerStateModel from 'src/types/state-models/account-drawer.state.model';

// components
import {
    CardRounded,
    IconActivity,
    IconDeclined,
    IconHide,
    IconInfo,
    IconLaunch,
    IconShow,
    IconTrash
} from '@americanexpress/dls-react';
import IconLink from '../../components/icon-link/IconLink';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { LinkColorVariants } from 'src/types/link-color-variants.enum';
import { DLSSize } from 'src/types/dls-size.enum';
import UnifiedTransactionsTable from '../../components/atomic/organisms/unified-transactions/UnifiedTransactionsTable';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';

// icons and style
import { PRODUCT_NAME } from 'src/utils/constants';
import styles from './AccountDrawer.module.scss';
import { LocalDate } from '@js-joda/core';
import NicknameEditBox from 'src/components/nickname-edit-box/NicknameEditBox';
import Selectors from 'src/utils/selectors';

export default class AccountDrawer extends BasicDrawer<any, AccountDrawerStateModel> {
    /// /////////////////
    // Onced Methods //
    /// ////////////////
    private readonly handleDeleteInstitutionOnce: (institution: InstitutionModel) => void = _.once(
        async (institution: InstitutionModel) => await this.handleDeleteInstitution(institution)
    );

    public override state: AccountDrawerStateModel = {
        account: null as AccountModel,
        showNicknameBox: false as boolean,
        editNickname: false as boolean
    };

    public override componentDidMount(): void {
        if (this.props.drawerProps.currentAccount) {
            this.setState({
                account: this.props.drawerProps.currentAccount,
                showNicknameBox:
                    this.props.drawerProps.currentAccount.name !== this.props.drawerProps.currentAccount.officialName,
                editNickname: this.isVisible(this.props.drawerProps.currentAccount)
            });
        }
    }

    public override render(): ReactComponentElement<any> {
        const account = this.state.account;
        if (!account) {
            return null;
        }

        let institution = account.institution;
        if (!institution) {
            // deleted accounts don't have one, but they still have the id
            institution = _.find(this.props.user.institutions, { id: (account as any).institutionId });
        }

        let accountName: string = this.state.account.name;
        if (this.state.account.name === this.state.account.officialName) {
            accountName = '';
        }

        const drawerProps: any = this.props.drawerProps;

        const accountActions: JSX.Element = (
            <CardRounded className={styles.accountActions}>
                <div className={styles.sections}>
                    <div className={styles.sectionHeader}>
                        <Typography variant={TypographyVariants.HEADING_XS}>
                            {LiteralsService.getLiteral('AccountDrawer.accountActionsHeader')}
                        </Typography>
                    </div>
                    {institution?.loginRequired ? (
                        <React.Fragment>
                            <IconLink
                                icon={<IconDeclined />}
                                onClick={() => this.props.handleReconnectInstitution(institution)}
                                text={LiteralsService.getLiteral('AccountDrawer.editLogin')}
                                typographyVariant={TypographyVariants.SUBTEXT}
                                linkColor={LinkColorVariants.ERROR}
                            />
                            <Typography variant={TypographyVariants.LEGAL_PRIMARY}>
                                {LiteralsService.getLiteral('AccountDrawer.issueMessage')}
                            </Typography>
                        </React.Fragment>
                    ) : null}
                    <div className={`${styles.applyAction} ${styles.borderUnder}`}>
                        <IconLaunch className={styles.accountActionIcon} />

                        <div className={styles.frameOne}>
                            <Typography variant={TypographyVariants.BODY_BOLD} className={styles.accountActionTitle}>
                                {LiteralsService.getLiteral('AccountDrawer.rewardsCheckingTitle')}
                            </Typography>
                            <Typography variant={TypographyVariants.LEGAL_PRIMARY} className={styles.infoSubtext}>
                                {LiteralsService.getLiteral('AccountDrawer.rewardsCheckingSubtext')}
                            </Typography>
                        </div>
                    </div>
                    {this.isVisible(account) ? (
                        <div className={`${styles.applyAction} ${styles.borderUnder}`}>
                            <IconHide className={styles.accountActionIcon} />
                            <div className={styles.frameOne}>
                                <Anchor
                                    data-cy={Selectors.PlanAhead.HIDE_ACCOUNT_BUTTON}
                                    onClick={async () => await this.hideAccount(account)}
                                    typographyProps={{
                                        variant: TypographyVariants.BODY_BOLD,
                                        className: styles.accountActionTitle
                                    }}
                                >
                                    {LiteralsService.getLiteral('AccountDrawer.hideAccountTitle')}
                                </Anchor>
                                <Typography variant={TypographyVariants.LEGAL_PRIMARY} className={styles.infoSubtext}>
                                    Hide this account from {PRODUCT_NAME} and remove its values from all projections.
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        <div className={`${styles.applyAction} ${styles.borderUnder}`}>
                            <IconShow className={styles.accountActionIcon} />
                            <div className={styles.frameOne}>
                                <Anchor
                                    data-cy={Selectors.PlanAhead.SHOW_ACCOUNT_BUTTON}
                                    onClick={async () => await this.showAccount(account)}
                                    typographyProps={{
                                        variant: TypographyVariants.BODY_BOLD,
                                        className: styles.accountActionTitle
                                    }}
                                >
                                    {LiteralsService.getLiteral('AccountDrawer.showAccountTitle')}
                                </Anchor>
                                <Typography variant={TypographyVariants.LEGAL_PRIMARY} className={styles.infoSubtext}>
                                    Show this account in {PRODUCT_NAME} and add its values to all projections.
                                </Typography>
                            </div>
                        </div>
                    )}

                    {
                        // waiting on microtransaction code
                        // <React.Fragment>
                        //     <Typography variant={TypographyVariants.SUBTEXT} className="warning-label after12
                        // textStrong" key="show"> <InfoIcon className="info-icon icon"/>Pending verification
                        // </Typography> <Typography variant={TypographyVariants.LEGAL_PRIMARY} className="textDefault
                        // after32" key="show-desc"> {PRODUCT_NAME} is waiting for Plaid to verify your account
                        // </Typography>  <Typography variant={TypographyVariants.SUBTEXT} className="info-label
                        // after12 textStrong" key="verify"> <InfoIcon className="info-icon icon"/>Add manual
                        // verification </Typography> <Typography variant={TypographyVariants.LEGAL_PRIMARY}
                        // className="textDefault after32" key="verify-desc"> Add the micro-deposits from Plaid to
                        // verify your account </Typography>  <Typography variant={TypographyVariants.SUBTEXT}
                        // className="error-label after12 textStrong" key="retry"> <InfoIcon className="info-icon
                        // icon"/>Retry verification </Typography> <Typography variant={TypographyVariants.LEGAL_PRIMARY} className="textDefault after32" key="retry-desc"> Your Plaid verification has expired </Typography> </React.Fragment>
                    }
                    <div className={styles.applyAction}>
                        <IconTrash className={styles.accountActionIcon} />
                        <div className={styles.frameOne}>
                            <Anchor
                                onClick={() => {
                                    this.props.handleOpenConfirmation({
                                        onAction: () => this.handleDeleteInstitutionOnce(institution),
                                        action: 'delete',
                                        object: 'imported'
                                    });
                                }}
                                typographyProps={{
                                    variant: TypographyVariants.BODY_BOLD,
                                    className: styles.accountActionTitle
                                }}
                            >
                                {LiteralsService.getLiteral('AccountDrawer.deleteInstitutionTitle')}
                            </Anchor>
                            <Typography variant={TypographyVariants.LEGAL_PRIMARY} className={styles.infoSubtext}>
                                {LiteralsService.getLiteral('AccountDrawer.deleteInstitutionSubtext')}
                            </Typography>
                        </div>
                    </div>
                </div>
            </CardRounded>
        );

        const accountDetails: JSX.Element = (
            <CardRounded className={styles.accountDetails}>
                <div className={styles.sections}>
                    {!this.isVisible(account) ? (
                        <div data-cy={Selectors.General.HIDDEN_ALERT} className={styles.alert}>
                            <IconInfo size={DLSSize.SMALL} className={styles.infoIcon} />
                            <Typography variant={TypographyVariants.BODY} className={styles.alertMessage}>
                                {LiteralsService.getLiteral('AccountDrawer.hiddenAlertMessage')}
                            </Typography>
                        </div>
                    ) : null}
                    <div className={styles.nibContainer}>
                        <div className={styles.leftColumn}>
                            <div className={styles.nameContainer}>
                                <Typography variant={TypographyVariants.BODY_BOLD}>
                                    {LiteralsService.getLiteral('AccountDrawer.name')}
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.HEADING_S}
                                    className={styles.accountDetailsValue}
                                    data-cy={Selectors.PlanAhead.ACCOUNT_NAME}
                                >
                                    {account.officialName}
                                </Typography>
                                {!this.state.showNicknameBox && this.state.editNickname ? (
                                    <React.Fragment>
                                        <Anchor
                                            onClick={this.showNicknameBox.bind(this)}
                                            typographyProps={{
                                                variant: TypographyVariants.LEGAL_PRIMARY,
                                                className: styles.accountAction
                                            }}
                                        >
                                            {LiteralsService.getLiteral('AccountDrawer.addNickname')}
                                        </Anchor>
                                    </React.Fragment>
                                ) : null}
                            </div>
                            {this.state.showNicknameBox && this.state.editNickname && (
                                <NicknameEditBox
                                    label={LiteralsService.getLiteral('AccountDrawer.nickname')}
                                    placeholder={LiteralsService.getLiteral('AccountDrawer.enterNickname')}
                                    nickname={accountName}
                                    handleSave={this.handleSaveNickname.bind(this)}
                                    handleSecondaryButtonClick={this.handleDeleteNickname.bind(this)}
                                    secondaryButtonLabel={LiteralsService.getLiteral('CommonText.delete')}
                                />
                            )}
                            <div className={styles.institutionContainer}>
                                <Typography variant={TypographyVariants.BODY_BOLD}>
                                    {LiteralsService.getLiteral('AccountDrawer.institution')}
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.HEADING_S}
                                    className={styles.accountDetailsValue}
                                >
                                    {institution?.name + (account.mask ? ' #' + account.mask : '')}
                                </Typography>
                            </div>
                        </div>
                        <div className={styles.balanceContainer}>
                            <Typography variant={TypographyVariants.BODY_BOLD}>
                                {LiteralsService.getLiteral('AccountDrawer.balance')}
                            </Typography>
                            <Typography variant={TypographyVariants.HEADING_XL} className={styles.accountDetailsValue}>
                                {FormatService.formatSummary(account.current)}
                            </Typography>
                        </div>
                    </div>
                </div>
            </CardRounded>
        );

        const latestTransactions: JSX.Element = (
            <CardRounded className={styles.latestTransactions}>
                <div className={styles.sections}>
                    <div className={styles.sectionHeader}>
                        <Typography variant={TypographyVariants.HEADING_XS}>
                            {LiteralsService.getLiteral('AccountDrawer.latestTransactionsHeader')}
                        </Typography>
                    </div>
                    {this.isVisible(account) ? (
                        <React.Fragment>
                            <UnifiedTransactionsTable
                                drawerProps={drawerProps}
                                showHidden={false}
                                saveLabel={'Save'}
                                handleBack={this.props.handleBack}
                                handleChangeIncomeSource={this.props.transactionFactory.handleChangeIncomeSource.bind(
                                    this.props.transactionFactory
                                )}
                                handleDisplayTransaction={this.props.transactionFactory.handleDisplayTransaction.bind(
                                    this.props.transactionFactory
                                )}
                                handleIgnoreFutureTransaction={this.props.transactionFactory.handleIgnoreFutureTransaction.bind(
                                    this.props.transactionFactory
                                )}
                                handleSortUnifiedTransaction={this.props.transactionFactory.handleSortUnifiedTransaction.bind(
                                    this.props.transactionFactory
                                )}
                                handleChangeTransactionGroup={this.props.transactionFactory.handleChangeTransactionGroup.bind(
                                    this.props.transactionFactory
                                )}
                                recategorizeSelectedIncomes={this.props.transactionFactory.recategorizeSelectedIncomes.bind(
                                    this.props.transactionFactory
                                )}
                                recategorizeSelectedExpenses={this.props.transactionFactory.recategorizeSelectedExpenses.bind(
                                    this.props.transactionFactory
                                )}
                                changeFrequenceOfSelected={this.props.transactionFactory.changeFrequenceOfSelected.bind(
                                    this.props.transactionFactory
                                )}
                                selectedTransactions={drawerProps.selectedTransactions || []}
                                transactions={drawerProps.displayedTransactions || []}
                                isReview={false}
                                updateDrawer={this.props.updateDrawer}
                                user={this.props.user}
                                handleDeleteIncomeRule={this.props.handleDeleteIncomeRule}
                                handleDeleteRule={this.props.handleDeleteRule}
                                updateFilteredTransactions={this.props.transactionFactory.updateFilteredTransactions.bind(
                                    this.props.transactionFactory
                                )}
                                review={false}
                                requireHeader={false}
                            />
                            <IconLink
                                icon={<IconActivity />}
                                onClick={async () =>
                                    await NavigationService.goTo(
                                        this.props.history,
                                        `/drawer/transactions?page=1&account=${account.id}`,
                                        'Transactions drawer'
                                    )
                                }
                                text={LiteralsService.getLiteral('AccountDrawer.viewTransactions')}
                                typographyClassName={styles.after32}
                            />
                        </React.Fragment>
                    ) : (
                        <div className={styles.emptyStateBox}>
                            <Typography variant={TypographyVariants.BODY_BOLD} className={styles.emptyStateContent}>
                                {LiteralsService.getLiteral('AccountDrawer.emptyStateBody')}
                            </Typography>
                            <div className={styles.showAccountTextLink}>
                                <Typography variant={TypographyVariants.SUBTEXT} className={styles.emptyStateContent}>
                                    <Anchor
                                        onClick={async () => await this.showAccount(account)}
                                        typographyProps={{
                                            variant: TypographyVariants.SUBTEXT,
                                            className: styles.emptyStateLink
                                        }}
                                    >
                                        {LiteralsService.getLiteral('AccountDrawer.emptyStateSubtextLink')}
                                    </Anchor>
                                    {LiteralsService.getLiteral('AccountDrawer.emptyStateSubtextNonLink')}
                                </Typography>
                            </div>
                        </div>
                    )}
                </div>
            </CardRounded>
        );

        return (
            <WideView
                classNameView="account-drawer"
                title={LiteralsService.getLiteral('AccountDrawer.pageTitle')}
                subtitle={this.state.account.officialName}
                hasBackgroundBanner
                showFooter
                history={this.props.drawerProps.history}
                handleBack={this.props.handleBack}
                data-cy={Selectors.PlanAhead.ACCOUNT_DRAWER}
            >
                <div className={styles.accountInfo}>
                    <div className={styles.leftColumn}>
                        {accountDetails}
                        <div className={styles.middleDisplay}>{accountActions}</div>
                        {latestTransactions}
                    </div>
                    <div className={styles.rightColumn}>{accountActions}</div>
                </div>
            </WideView>
        );
    }

    private async handleSaveNickname(values: { name: string }): Promise<void> {
        const account: AccountModel = { ...this.state.account };
        account.name = values.name;
        await AccountService.updateAccount(account);
        await this.props.handleReloadUser();
    }

    private async handleCancel(): Promise<void> {
        const account: AccountModel = { ...this.state.account };
        account.name = '';

        this.setState({
            account,
            showNicknameBox: false
        });
    }

    private async handleDeleteNickname(): Promise<void> {
        await this.handleCancel();
        await this.handleSaveNickname({ name: '' });
    }

    private async handleDeleteInstitution(institution: InstitutionModel): Promise<void> {
        // need to await or else new (old) data is loaded before this actually deletes
        const numberOfAccounts: number = _.filter(this.props.user.accounts, (account: AccountModel) => {
            return account.institution.institutionId === institution.institutionId;
        }).length;

        this.props.showLoader(['Syncing your accounts', 'This can take up to a minute.']);
        try {
            await InstitutionService.deleteInstitution(institution);
        } catch (e) {
            console.log(e);
        }
        this.props.hideLoader();

        MixpanelService.track('Delete Institution', {
            'Number of Institutions': this.props.user.institutions.length - 1,
            'Number of Accounts': numberOfAccounts
        });

        await NavigationService.goBack(this.props.drawerProps.history);
        this.props.handleReloadUser();
    }

    private updateAccount(newAccount: AccountModel): void {
        this.setState({
            account: newAccount,
            editNickname: true
        });
    }

    private isVisible(account: AccountModel): boolean {
        return !_.find(this.props.drawerProps.hiddenAccounts, { accountId: account.accountId });
    }

    private async hideAccount(account: AccountModel): Promise<void> {
        await AccountService.hideAccount(account.id);
        await this.props.handleReloadUser();
        this.props.updateDrawer({
            ...this.props.drawerProps,
            hiddenAccounts: await AccountService.hiddenAccounts(),
            currentAccount: account
        });
        this.canEditNickname(false);
    }

    private async showAccount(account: AccountModel): Promise<void> {
        this.props.showLoader(['Syncing your accounts', 'This can take up to a minute.']);
        await AccountService.showAccount(account.accountId);
        await AccountService.hiddenAccounts();
        await this.props.handleReloadUser();
        this.props.hideLoader();
        const newAccount: AccountModel = await this.updateTransactionsRoute(account);
        this.updateAccount(newAccount);
    }

    private async updateTransactionsRoute(account: AccountModel): Promise<AccountModel> {
        const newAccount: AccountModel = _.find(this.props.user.accounts, { accountId: account.accountId });
        const state: string = 'account';
        const hiddenAccounts: AccountModel[] = await AccountService.hiddenAccounts();
        const searchParams = new URL(window.location.href).searchParams;
        const startDate: LocalDate = searchParams.get('start') ? LocalDate.parse(searchParams.get('start')) : undefined;
        const endDate: LocalDate = searchParams.get('end') ? LocalDate.parse(searchParams.get('end')) : undefined;

        const filters = {
            account: newAccount.id,
            currentAccount: newAccount,
            incomeSource: searchParams.get('incomesource'),
            hiddenAccounts,
            group: searchParams.get('group'),
            search: searchParams.get('search'),
            selections: searchParams.get('selections'),
            state: 'account',
            type: searchParams.get('type')
        };

        await this.props.transactionFactory.openTransactionsRoute(
            startDate,
            endDate,
            {
                filters
            },
            state
        );
        return newAccount;
    }

    private showNicknameBox(): void {
        this.setState({
            showNicknameBox: true
        });
    }

    private canEditNickname(editNickname: boolean): void {
        this.setState({
            editNickname
        });
    }
}
